import React, { ReactNode, useState, useEffect, PropsWithChildren } from 'react';
import styled, { CSSProperties } from 'styled-components';
import constants from '../common/theme/constants';
import { hasValue, toBoolean } from '../common/utils/KitData';
import KitButton from './KitButton';
import KitIcon from './KitIcon';

const ToggleRow = styled.tr`
  cursor: pointer;
`;

const ToggleAdditionalInfoRow = styled.tr`
  visibility: collapse;
`;

const MaxWidthTd = styled.td`
  width: 25px;
  max-width: 25px;
`;

const AdditionalInfoDiv = styled.div`
  width: 100%;
  padding: ${constants.spacing.absolute.s}px;
`;

const HiddenText = styled.span`
  display: none;
`;

export interface KitExpandableRowProps extends PropsWithChildren {
  id: string;
  colCount?: number;
  additionalInfo?: ReactNode;
  hasToggle?: boolean;
  onToggle?: (expanded: boolean) => void;
  toggleAlign?: string;
  ariaProps: {
    'aria-label': string;
  };
}

const KitExpandableRow: React.FC<KitExpandableRowProps> = ({
  id,
  children,
  colCount,
  additionalInfo,
  hasToggle,
  // default to right-aligned, per UX guidance
  toggleAlign = 'right',
  onToggle,
  ariaProps = {},
}) => {
  const [isExpanded, setExpanded] = useState(null);
  const toggleRowStyles: CSSProperties = isExpanded
    ? {
        visibility: 'visible',
        display: 'table-row',
      }
    : {
        visibility: 'collapse',
        display: 'none',
      };

  useEffect(() => {
    if (hasToggle && onToggle && hasValue(isExpanded)) {
      onToggle(isExpanded);
    }
  }, [isExpanded, hasToggle, onToggle]);

  return (
    <>
      {hasToggle ? (
        <>
          <ToggleRow
            onClick={() => setExpanded(!toBoolean(isExpanded))}
            role='row'
            aria-controls={`addition-info-${id}`}
            aria-expanded={isExpanded}
          >
            <>
              {toggleAlign === 'right' && children}
              <MaxWidthTd>
                <KitButton
                  variant='circle'
                  title={ariaProps['aria-label']}
                  aria-label={ariaProps['aria-label']}
                >
                  <HiddenText>{ariaProps['aria-label']}</HiddenText>
                  <KitIcon
                    icon={isExpanded ? 'angle-up' : 'angle-down'}
                    size={`${constants.iconSize.xs}rem`}
                  />
                </KitButton>
              </MaxWidthTd>
              {toggleAlign === 'left' && children}
            </>
          </ToggleRow>
          <ToggleAdditionalInfoRow style={toggleRowStyles} id={`addition-info-${id}`}>
            <td colSpan={colCount + 1}>
              <AdditionalInfoDiv>{additionalInfo}</AdditionalInfoDiv>
            </td>
          </ToggleAdditionalInfoRow>
        </>
      ) : (
        <tr>{children}</tr>
      )}
    </>
  );
};
export default KitExpandableRow;
